import * as React from 'react';
import { connect } from 'react-redux';
import { AppStates } from '../../reducers/app';
import { DrawingToolState, DrawingAttr } from '../../drawing/reducers/drawingTool';
import SettingPopup from '../common/SettingPopup';
import ToolIconButton from '../common/ToolIconButton';
import { DrawingMode, FontType } from '../../drawing/types';
import { getBasePath } from '../../utils/browser';

type SelectableDrawingMode = DrawingMode.StickyDrawing | DrawingMode.BalloonDrawing;

interface MaskAndBalloonSettingsDialogProps {
  isOpen: boolean;
  onClose: (drawingAttr: DrawingAttr | null, selectedMode: SelectableDrawingMode | null) => void;
}

interface MaskAndBalloonSettingsDialogOwnProps {
  drawingTool: DrawingToolState;
}

type MaskAndBalloonSettingsDialogPropsType =
  MaskAndBalloonSettingsDialogProps & MaskAndBalloonSettingsDialogOwnProps;

interface MaskAndBalloonSettingsDialogState {
  drawingAttr: DrawingAttr;
  // FIXME : ComponentがUnmountされたら直前の設定が飛ぶ
  selectedMode: SelectableDrawingMode;
}

enum Color {
  Red = 'red',
  Blue = 'blue',
  Yellow = 'yellow',
  Green = 'green',
  Black = 'black',
  Transparent = 'transparent',
}

const okButtonStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '20px',
  right: '20px',
  display: 'block',
  width: '110px',
  height: '38px',
  lineHeight: '38px',
  color: '#fff',
  background: '#333',
  textAlign: 'center',
};

const iconStyle: React.CSSProperties = {
  display: 'block',
  width: '48px',
  height: '48px',
  margin: '8px',
  flexGrow: 0,
  backgroundColor: '#fff',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const rowLabelStyle: React.CSSProperties = {
  flex: 'none',
  width: '5em',
  textAlign: 'right',
  margin: '0.75em',
};

const rowContentStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const colorChipStyle: React.CSSProperties = {
  margin: 0,
  backgroundColor: 'transparent',
};

const labelButtonStyle: React.CSSProperties = {
  width: '120px',
  backgroundColor: '#ebebeb',
};

const labelButtonImageStyle: React.CSSProperties = {
  maxWidth: '120px',
  maxHeight: '45px',
};

const toolTypeButtonStyle: React.CSSProperties = {
  margin: 0,
  width: '64px',
  height: '64px',
  padding: '8px',
  backgroundColor: 'transparent',
};

class MaskAndBalloonSettingsDialog extends React.Component<
  MaskAndBalloonSettingsDialogPropsType,
  MaskAndBalloonSettingsDialogState
  > {
  private readonly baseDir: string;

  public constructor(props: MaskAndBalloonSettingsDialogPropsType) {
    super(props);
    this.state = {
      // XXX: マスクと吹き出しの共通設定の参照先として、適当にマスクを選択している
      drawingAttr: props.drawingTool.drawingAttrs[DrawingMode.StickyDrawing],
      // XXX : 既定値はマスク(付箋)としてみる
      selectedMode: DrawingMode.StickyDrawing,
    };
    this.baseDir = getBasePath();
  }

  public componentWillReceiveProps(nextProps: MaskAndBalloonSettingsDialogPropsType): void {
    this.setState({ drawingAttr: nextProps.drawingTool.drawingAttrs[DrawingMode.StickyDrawing] });
  }

  private handleOnClose = () => {
    this.props.onClose(null, this.state.selectedMode);
  }

  private handleOnOK = () => {
    this.props.onClose(this.state.drawingAttr, this.state.selectedMode);
  }

  private setParamToState = (params: Partial<DrawingAttr>) => {
    this.setState({ drawingAttr: { ...this.state.drawingAttr, ...params } });
  }

  private handlePaintColorChange = (fillColor: Color, isTransparent: boolean) => {
    const isFilled = fillColor === Color.Transparent ? false : true;
    this.setParamToState({ fillColor, isTransparent, isFilled });
  }

  private handleLineColorChange = (lineColor: string) => {
    this.setParamToState({ lineColor });
  }

  private handleFontTypeChange = (fontType: FontType) => {
    this.setParamToState({ fontType });
  }

  private handleFontSizeChange = (fontSize: number) => {
    this.setParamToState({ fontSize });
  }

  private handleTextColorChange = (textColor: string) => {
    this.setParamToState({ textColor });
  }

  public render() {
    if (!this.props.isOpen) {
      return null;
    }

    const attrs = this.state.drawingAttr;
    return (
      <SettingPopup title="メモ設定" onClose={this.handleOnClose}>
        <div style={{ display: 'flex', margin: '0.75em' }}>
          <img style={iconStyle} src={`${this.baseDir}/images/tool-icons/mask-balloon.png`} />
          <div>
            <div style={rowStyle}>
              <div style={rowLabelStyle}>塗り色</div>
              <div style={rowContentStyle}>
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.fillColor === Color.Red && !attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Red, false)}
                  iconName="colorchip-square-solid-red" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.fillColor === Color.Blue && !attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Blue, false)}
                  iconName="colorchip-square-solid-blue" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.fillColor === Color.Yellow && !attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Yellow, false)}
                  iconName="colorchip-square-solid-yellow" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.fillColor === Color.Green && !attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Green, false)}
                  iconName="colorchip-square-solid-green" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.fillColor === Color.Black && !attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Black, false)}
                  iconName="colorchip-square-solid-black" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.fillColor === Color.Transparent && !attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Transparent, false)}
                  iconName="colorchip-square-solid-transparent" />
              </div>
            </div>
            <hr />
            <div style={rowStyle}>
              <div style={rowLabelStyle}>半透化色</div>
              <div style={rowContentStyle}>
                <ToolIconButton
                  style={{ ...colorChipStyle, opacity: 0.5 }}
                  active={attrs.fillColor === Color.Red && attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Red, true)}
                  iconName="colorchip-square-solid-red" />
                <ToolIconButton
                  style={{ ...colorChipStyle, opacity: 0.5 }}
                  active={attrs.fillColor === Color.Blue && attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Blue, true)}
                  iconName="colorchip-square-solid-blue" />
                <ToolIconButton
                  style={{ ...colorChipStyle, opacity: 0.5 }}
                  active={attrs.fillColor === Color.Yellow && attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Yellow, true)}
                  iconName="colorchip-square-solid-yellow" />
                <ToolIconButton
                  style={{ ...colorChipStyle, opacity: 0.5 }}
                  active={attrs.fillColor === Color.Green && attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Green, true)}
                  iconName="colorchip-square-solid-green" />
                <ToolIconButton
                  style={{ ...colorChipStyle, opacity: 0.5 }}
                  active={attrs.fillColor === Color.Black && attrs.isTransparent}
                  onClick={() => this.handlePaintColorChange(Color.Black, true)}
                  iconName="colorchip-square-solid-black" />
              </div>
            </div>
            <hr />
            <div style={rowStyle}>
              <div style={rowLabelStyle}>線色</div>
              <div style={rowContentStyle}>
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.lineColor === Color.Red}
                  onClick={() => this.handleLineColorChange(Color.Red)}
                  iconName="colorchip-square-border-red" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.lineColor === Color.Blue}
                  onClick={() => this.handleLineColorChange(Color.Blue)}
                  iconName="colorchip-square-border-blue" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.lineColor === Color.Yellow}
                  onClick={() => this.handleLineColorChange(Color.Yellow)}
                  iconName="colorchip-square-border-yellow" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.lineColor === Color.Green}
                  onClick={() => this.handleLineColorChange(Color.Green)}
                  iconName="colorchip-square-border-green" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.lineColor === Color.Black}
                  onClick={() => this.handleLineColorChange(Color.Black)}
                  iconName="colorchip-square-border-black" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.lineColor === Color.Transparent}
                  onClick={() => this.handleLineColorChange(Color.Transparent)}
                  iconName="colorchip-square-border-transparent" />
              </div>
            </div>
            <hr />
            <div style={rowStyle}>
              <div style={rowLabelStyle}>文字</div>
              <div style={rowContentStyle}>
                <ToolIconButton
                  style={labelButtonStyle}
                  active={attrs.fontType === FontType.Mincho}
                  onClick={() => this.handleFontTypeChange(FontType.Mincho)}
                  imageStyle={labelButtonImageStyle}
                  iconName="font-family-serif" />
                <ToolIconButton
                  style={labelButtonStyle}
                  active={attrs.fontType === FontType.Gothic}
                  onClick={() => this.handleFontTypeChange(FontType.Gothic)}
                  imageStyle={labelButtonImageStyle}
                  iconName="font-family-sans-serif" />
                <ToolIconButton
                  style={labelButtonStyle}
                  active={attrs.fontType === FontType.Gothic /* STUB */}
                  onClick={() => this.handleFontTypeChange(FontType.Gothic) /* STUB */}
                  imageStyle={labelButtonImageStyle}
                  iconName="font-family-normal" />
              </div>
            </div>
            <hr />
            <div style={rowStyle}>
              <div style={rowLabelStyle}>文字サイズ</div>
              <div style={rowContentStyle}>
                <ToolIconButton
                  style={labelButtonStyle}
                  active={attrs.fontSize === 200}
                  onClick={() => this.handleFontSizeChange(200)}
                  imageStyle={labelButtonImageStyle}
                  iconName="font-size-large" />
                <ToolIconButton
                  style={labelButtonStyle}
                  active={attrs.fontSize === 100}
                  onClick={() => this.handleFontSizeChange(100)}
                  imageStyle={labelButtonImageStyle}
                  iconName="font-size-medium" />
                <ToolIconButton
                  style={labelButtonStyle}
                  active={attrs.fontSize === 50}
                  onClick={() => this.handleFontSizeChange(50)}
                  imageStyle={labelButtonImageStyle}
                  iconName="font-size-small" />
              </div>
            </div>
            <hr />
            <div style={rowStyle}>
              <div style={rowLabelStyle}>文字色</div>
              <div style={rowContentStyle}>
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.textColor === Color.Red}
                  onClick={() => this.handleTextColorChange(Color.Red)}
                  iconName="text-color-red" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.textColor === Color.Blue}
                  onClick={() => this.handleTextColorChange(Color.Blue)}
                  iconName="text-color-blue" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.textColor === Color.Yellow}
                  onClick={() => this.handleTextColorChange(Color.Yellow)}
                  iconName="text-color-yellow" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.textColor === Color.Green}
                  onClick={() => this.handleTextColorChange(Color.Green)}
                  iconName="text-color-green" />
                <ToolIconButton
                  style={colorChipStyle}
                  active={attrs.textColor === Color.Black}
                  onClick={() => this.handleTextColorChange(Color.Black)}
                  iconName="text-color-black" />
              </div>
            </div>
            <hr />
            <div style={rowStyle}>
              <div style={rowLabelStyle}>枠の種類</div>
              <div style={rowContentStyle}>
                <ToolIconButton
                  onClick={() => {
                    this.setState({ selectedMode: DrawingMode.StickyDrawing });
                  }}
                  active={this.state.selectedMode === DrawingMode.StickyDrawing}
                  style={toolTypeButtonStyle}
                  title="ふせん"
                  ariaLabel="tool-sticky"
                  iconName="tool-sticky" />
                <ToolIconButton
                  onClick={() => {
                    this.setState({ selectedMode: DrawingMode.BalloonDrawing });
                  }}
                  active={this.state.selectedMode === DrawingMode.BalloonDrawing}
                  style={toolTypeButtonStyle}
                  title="吹き出し"
                  ariaLabel="tool-balloon"
                  iconName="tool-balloon" />
              </div>
            </div>
          </div>
        </div>

        <a style={okButtonStyle} onClick={this.handleOnOK}>OK</a>
      </SettingPopup>
    );
  }
}

const mapStateToProps = (state: AppStates) => ({ drawingTool: state.drawingTool });

export default connect(mapStateToProps)(MaskAndBalloonSettingsDialog);
